/** Typed admin API client.
 *
 *  SECURITY (audit 2026-09-25, F-22): the access token lives in memory only (never in
 *  localStorage); the refresh token is an httpOnly, Secure, SameSite=Strict cookie scoped to
 *  /api/admin/v1/auth that JavaScript cannot read. On 401 the client refreshes once through
 *  that cookie (the server rotates it) and retries; a reload restores the session the same
 *  way (`restoreSession`).
 *
 *  Step-up 2FA (audit 2026-10-22, src/api/stepup.py): a dangerous action (payout, refund, big
 *  credit, admins, gateways, kill switches) answers 403 with `X-Step-Up: required | invalid`
 *  until the request carries a fresh code in `X-Admin-TOTP`. `send` then asks the registered
 *  handler (StepUpHost: the 6-digit dialog) for a code and repeats the same request with it,
 *  so pages call `api.post(...)` as before and need no changes. Cancel → StepUpCancelled. */

import { units } from '@/lib/format'

const BASE = '/api/admin/v1'
// Pre-audit builds kept tokens here; wipe them on load so nothing lingers in storage.
const LEGACY_KEYS = ['nh_admin_access', 'nh_admin_refresh', 'nh_admin_role']

/** A failed request. The server's words reach the screen as they are, except the credit unit:
 *  a ⚡ in them becomes the text presentation (`units`, design §6.14), so «Не больше 10 000 ⚡ за
 *  место» reads in the ink of its line, not as the yellow emoji outside the palette. */
export class ApiError extends Error {
  /** The server's own `detail` when it was a string (the message may be replaced by a
   *  generic text, e.g. for 423/429). */
  public detail?: string

  constructor(
    public status: number,
    message: string,
    detail?: string,
    /** `Retry-After` as words («15 мин», «30 с»), when the server sent one. */
    public retryAfter?: string | null,
  ) {
    super(units(message))
    this.detail = detail === undefined ? undefined : units(detail)
  }
}

/** The operator closed the 2FA dialog: the action was not sent. */
export class StepUpCancelled extends ApiError {
  constructor() {
    super(403, 'Действие не подтверждено кодом 2FA', 'stepup_cancelled')
  }
}

export const STEPUP_HEADER = 'X-Admin-TOTP'

/** What the dialog shows for one ask: the action (from `X-Step-Up-Purpose`) and the server's
 *  answer to the previous code, if any. */
export interface StepUpPrompt {
  purpose: string | null
  /** null on the first ask; the server's words after a wrong / used code */
  error: string | null
}

/** Opens the dialog and resolves with a code, or null when cancelled. `done` closes it once
 *  the request is settled: success, or any answer that is not «wrong code» (a lock, 503, a
 *  business error) — that one reaches the page as a normal ApiError. */
export interface StepUpHandler {
  ask: (prompt: StepUpPrompt) => Promise<string | null>
  done: () => void
}

let stepUpHandler: StepUpHandler | null = null

/** StepUpHost registers itself here; returns the unregister function. */
export function setStepUpHandler(handler: StepUpHandler | null): () => void {
  stepUpHandler = handler
  return () => {
    if (stepUpHandler === handler) stepUpHandler = null
  }
}

let accessToken: string | null = null
let currentRole: string | null = null

try {
  for (const k of LEGACY_KEYS) localStorage.removeItem(k)
} catch {
  /* storage blocked — nothing to clean */
}

export function getToken(): string | null {
  return accessToken
}

export function getRole(): string | null {
  return currentRole
}

export function setSession(token: string, role: string): void {
  accessToken = token
  currentRole = role
}

export function clearSession(): void {
  accessToken = null
  currentRole = null
}

function forceLogout(): void {
  clearSession()
  window.dispatchEvent(new Event('nh:unauthorized'))
}

interface TokenResponse {
  access_token: string
  role: string
}

async function postRefresh(): Promise<Response> {
  return fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
  })
}

// De-dupe concurrent refreshes: many queries can 401 at once; refresh only once.
let refreshing: Promise<TokenResponse | null> | null = null

/** Rotate the refresh cookie and get a fresh access token; null when the session is gone.
 *  409 means another tab rotated the cookie a moment ago — retry once with the new one. */
export function refreshSession(): Promise<TokenResponse | null> {
  if (refreshing) return refreshing
  refreshing = (async () => {
    try {
      let res = await postRefresh()
      if (res.status === 409) res = await postRefresh()
      if (!res.ok) return null
      const data = (await res.json()) as TokenResponse
      setSession(data.access_token, data.role)
      return data
    } catch {
      return null
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

/** On app start: silently restore the session from the refresh cookie, if any. */
export async function restoreSession(): Promise<TokenResponse | null> {
  return refreshSession()
}

type Extra = Record<string, string>

async function doFetch(method: string, path: string, token: string | null, body?: unknown, extra: Extra = {}) {
  return fetch(BASE + path, {
    method,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extra,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
}

function retryAfterText(res: Response): string | null {
  const s = Number(res.headers.get('Retry-After') ?? '')
  if (!Number.isFinite(s) || s <= 0) return null
  return s >= 60 ? `${Math.ceil(s / 60)} мин` : `${Math.ceil(s)} с`
}

/** FastAPI answers a body that does not validate with 422 and `detail` as a list
 *  (`[{loc: ['body', 'field'], msg, …}]`). Name the fields instead of a bare «Ошибка 422»
 *  (review INT-7). Anything else keeps the status-only text. */
export function validationText(status: number, detail: unknown): string {
  if (!Array.isArray(detail)) return `Ошибка ${status}`
  const fields = new Set<string>()
  for (const item of detail) {
    const loc = (item as { loc?: unknown } | null)?.loc
    const last = Array.isArray(loc) ? loc[loc.length - 1] : undefined
    if (typeof last === 'string' && last !== 'body') fields.add(last)
  }
  return fields.size > 0
    ? `Ошибка ${status}: неверное значение поля ${[...fields].join(', ')}`
    : `Ошибка ${status}`
}

/** One HTTP attempt with the given access token and extra headers (the 401 path calls it
 *  twice, the step-up path once more per code). */
type Attempt = (token: string | null, extra?: Extra) => Promise<Response>

/** The body of a successful response: JSON by default, `res.blob()` for files. */
type Parse<T> = (res: Response) => Promise<T>

async function readDetail(res: Response): Promise<unknown> {
  return res
    .clone()
    .json()
    .then((b) => (b as { detail?: unknown }).detail)
    .catch(() => undefined)
}

/** One request with the 401 refresh-once. */
async function authed(path: string, attempt: Attempt, extra: Extra): Promise<Response> {
  let res = await attempt(getToken(), extra)
  if (res.status === 401 && !path.startsWith('/auth/')) {
    const fresh = await refreshSession()
    if (fresh === null) {
      forceLogout()
      throw new ApiError(401, 'Требуется вход')
    }
    res = await attempt(fresh.access_token, extra) // retry once with the new token
    if (res.status === 401) {
      forceLogout()
      throw new ApiError(401, 'Требуется вход')
    }
  }
  return res
}

/** The step-up loop: while the server asks for a code (`required` / `invalid`) and a dialog is
 *  registered, ask for one and repeat the request with it. A code is single-use, so every
 *  repeat carries the newly typed one. */
async function withStepUp(path: string, attempt: Attempt): Promise<Response> {
  let res = await authed(path, attempt, {})
  let asked = false
  try {
    for (;;) {
      const kind = res.headers.get('X-Step-Up')
      if (!stepUpHandler || (kind !== 'required' && kind !== 'invalid')) break
      const detail = await readDetail(res)
      const code = await stepUpHandler.ask({
        purpose: res.headers.get('X-Step-Up-Purpose'),
        error: kind === 'invalid' && typeof detail === 'string' ? units(detail) : null,
      })
      asked = true
      if (code === null) throw new StepUpCancelled()
      res = await authed(path, attempt, { [STEPUP_HEADER]: code })
    }
  } finally {
    if (asked) stepUpHandler?.done()
  }
  return res
}

async function send<T>(path: string, attempt: Attempt, parse: Parse<T> = (res) => res.json() as Promise<T>): Promise<T> {
  const res = await withStepUp(path, attempt)

  if (!res.ok) {
    const detail = await res
      .json()
      .then((b) => (b as { detail?: unknown }).detail)
      .catch(() => undefined)
    let message = typeof detail === 'string' ? detail : validationText(res.status, detail)
    const wait = retryAfterText(res)
    if (res.status === 423 || res.status === 429) {
      message =
        res.status === 423
          ? `Вход временно заблокирован после неудачных попыток${wait ? `. Повторите через ${wait}` : ''}`
          : `Слишком много попыток${wait ? `. Повторите через ${wait}` : ''}`
    }
    throw new ApiError(res.status, message, typeof detail === 'string' ? detail : undefined, wait)
  }
  if (res.status === 204) return undefined as T
  return parse(res)
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  return send<T>(path, (token, extra) => doFetch(method, path, token, body, extra))
}

export type Query = Record<string, string | number | boolean | null | undefined>

/** `path` plus a query string; null / undefined values are left out. Never put secrets here
 *  (URLs end up in logs): only ids, roles and similar. */
export function withQuery(path: string, query?: Query): string {
  if (!query) return path
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined) continue
    qs.append(k, String(v))
  }
  const s = qs.toString()
  if (!s) return path
  return `${path}${path.includes('?') ? '&' : '?'}${s}`
}

/** POST a file as the RAW request body (the API has no multipart parser): the file's own
 *  type as Content-Type, the same bearer token and 401 refresh-once as every other call.
 *  A Blob can be sent again, so the retry after a refresh re-sends the same bytes. */
async function upload<T>(path: string, file: Blob, query?: Query): Promise<T> {
  const full = withQuery(path, query)
  return send<T>(full, (token, extra) =>
    fetch(BASE + full, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...extra,
      },
      body: file,
    }),
  )
}

/** GET a file (an SVG QR code, a CSV export) with the bearer token and the same 401
 *  refresh-once as every other call: a plain <img src> / <a href> cannot send the token. */
async function blob(path: string): Promise<Blob> {
  return send<Blob>(path, (token, extra) => doFetch('GET', path, token, undefined, extra), (res) => res.blob())
}

/** `blob` plus the response headers (a CSV export says `X-Export-Truncated`). */
async function blobWithHeaders(path: string): Promise<{ blob: Blob; headers: Headers }> {
  return send<{ blob: Blob; headers: Headers }>(
    path,
    (token, extra) => doFetch('GET', path, token, undefined, extra),
    async (res) => ({ blob: await res.blob(), headers: res.headers }),
  )
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  upload,
  blob,
  blobWithHeaders,
}
